import { useEffect, useRef, useState } from 'react';
import { Type, Upload, Download, Loader2, Copy, Check, GitBranch, Rocket } from 'lucide-react';
import { apiService } from '@/services/api.service';
import { PackSourceNote } from '@/components/config/PackSourceNote';
import { useGameStore } from '@/stores/game.store';
import styles from '@/pages/GradesPage.module.scss';

function readAsDataUrl(file: File): Promise<string> {
  return new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result as string);
    r.onerror = rej;
    r.readAsDataURL(file);
  });
}

/** Aperçu + remplacement de l'atlas de glyphes des grades (glyph_F1.png), avec grille cliquable
 * pour récupérer le tag (caractère) de chaque glyphe à coller dans les formats de chat. */
export function GlyphAtlasEditor({ page = 'F1' }: { page?: string }) {
  const project = useGameStore((s) => s.activeGameId); // l'atlas vient du pack glyphes du projet actif
  const [url, setUrl] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [feedback, setFeedback] = useState<{ ok: boolean; msg: string } | null>(null);
  const [reload, setReload] = useState(0);
  const [sel, setSel] = useState<{ row: number; col: number } | null>(null);
  const [copied, setCopied] = useState<string | null>(null);
  const [branch, setBranch] = useState<string | null>(null);
  const [publishing, setPublishing] = useState<'push' | 'main' | null>(null);
  const [log, setLog] = useState<string[]>([]);
  const fileRef = useRef<HTMLInputElement>(null);

  const base = parseInt(page, 16) * 256; // F1 -> 0xF100
  const cp = sel ? base + sel.row * 16 + sel.col : 0;
  const hex = cp.toString(16).toUpperCase().padStart(4, '0');
  const char = sel ? String.fromCodePoint(cp) : '';

  useEffect(() => {
    let active = true;
    let created: string | null = null;
    apiService.getGlyphAtlasBlobUrl(page).then((r) => {
      if (!active) { if (r) URL.revokeObjectURL(r); return; }
      created = r;
      setUrl(r);
    });
    return () => { active = false; if (created) URL.revokeObjectURL(created); };
  }, [page, reload, project]);

  useEffect(() => {
    let alive = true;
    apiService.getCosmeticSources(page).then((r) => {
      if (alive) setBranch(r.data?.glyph.branch ?? null);
    });
    return () => { alive = false; };
  }, [page, project]);

  /** Commit + push de l'atlas sur la branche de travail du pack (develop), sans PR vers main. */
  const publish = async () => {
    setPublishing('push');
    setFeedback(null);
    setLog([]);
    const res = await apiService.publishGlyphAtlas(page);
    setPublishing(null);
    if (!res.data) { setFeedback({ ok: false, msg: res.error ?? 'Échec' }); return; }
    setFeedback({ ok: res.data.success, msg: res.data.success ? `Atlas publié sur ${branch ?? 'la branche de travail'}.` : 'Publication en échec.' });
    setLog(res.data.messages ?? []);
  };

  /** Promotion vers main : PR develop→main + merge + tag de release du pack. */
  const deliver = async () => {
    setPublishing('main');
    setFeedback(null);
    setLog([]);
    const res = await apiService.deliverGlyphAtlas(page);
    setPublishing(null);
    if (!res.data) { setFeedback({ ok: false, msg: res.error ?? 'Échec' }); return; }
    setFeedback({ ok: res.data.success, msg: res.data.success ? 'Atlas promu sur main (PR mergée + tag de release).' : 'Promotion en échec.' });
    setLog(res.data.messages ?? []);
  };

  const copy = async (value: string, tag: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(tag);
      setTimeout(() => setCopied((c) => (c === tag ? null : c)), 1500);
    } catch {
      setFeedback({ ok: false, msg: 'Copie impossible (presse-papiers refusé).' });
    }
  };

  const onPick = async (f: File) => {
    if (!f.type.includes('png')) { setFeedback({ ok: false, msg: 'Le fichier doit être un PNG.' }); return; }
    setBusy(true);
    setFeedback(null);
    try {
      const dataUrl = await readAsDataUrl(f);
      const res = await apiService.replaceGlyphAtlas(page, dataUrl, f.name);
      setFeedback({
        ok: res.success,
        msg: res.success
          ? `Atlas remplacé sur le serveur. Publie-le sur ${branch ?? 'la branche de travail'}, puis mets-le sur main pour la release.`
          : res.message,
      });
      setLog([]);
      if (res.success) setReload((k) => k + 1);
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  return (
    <div className={styles.detailInner}>
      <div className={styles.detailHeader}>
        <div className={styles.detailIdentity}>
          <Type size={22} className={styles.detailIcon} />
          <span className={styles.detailName}>Table de glyphes des grades (glyph_{page})</span>
        </div>
        <PackSourceNote role="glyph" page={page} />
      </div>

      <p className={styles.muted}>
        Grille 16×16 des glyphes de la page {page} (U+{base.toString(16).toUpperCase()}..U+{(base + 255).toString(16).toUpperCase()}).
        Clique une case pour récupérer son tag (le caractère à coller dans un format de chat). Remplace le PNG pour modifier les glyphes.
      </p>

      <div className={styles.glyphGridWrap}>
        {url ? (
          <>
            <img src={url} alt={`glyph_${page}`} className={styles.glyphGridImg} />
            <div className={styles.glyphGrid}>
              {Array.from({ length: 256 }, (_, i) => {
                const row = Math.floor(i / 16);
                const col = i % 16;
                const active = sel?.row === row && sel?.col === col;
                const c = base + i;
                return (
                  <button
                    key={i}
                    type="button"
                    className={`${styles.glyphCell} ${active ? styles.glyphCellActive : ''}`}
                    title={`U+${c.toString(16).toUpperCase()}`}
                    onClick={() => setSel({ row, col })}
                  />
                );
              })}
            </div>
          </>
        ) : (
          <span className={styles.cosmeticMissing}>Atlas glyph_{page}.png introuvable</span>
        )}
      </div>

      {sel && url && (
        <div className={styles.glyphInfo}>
          <div
            className={styles.glyphInfoPreview}
            style={{ backgroundImage: `url(${url})`, backgroundPosition: `${(sel.col / 15) * 100}% ${(sel.row / 15) * 100}%` }}
          />
          <div className={styles.glyphInfoBody}>
            <div className={styles.muted}>Ligne {sel.row} · Colonne {sel.col} · <code>U+{hex}</code></div>
            <div className={styles.glyphInfoActions}>
              <button className={styles.secondaryBtn} onClick={() => copy(char, 'char')}>
                {copied === 'char' ? <Check size={14} /> : <Copy size={14} />} Copier le glyphe
              </button>
              <button className={styles.secondaryBtn} onClick={() => copy(`\\u${hex}`, 'esc')}>
                {copied === 'esc' ? <Check size={14} /> : <Copy size={14} />} Copier \u{hex}
              </button>
            </div>
            <div className={styles.muted}>Colle le glyphe copié directement dans un format de chat (Supervision &gt; Grades, onglet Cosmétiques d'un grade).</div>
          </div>
        </div>
      )}

      <div className={styles.essActions}>
        <input
          ref={fileRef}
          type="file"
          accept="image/png"
          style={{ display: 'none' }}
          onChange={(e) => { const f = e.target.files?.[0]; if (f) onPick(f); }}
        />
        <button className={styles.primaryBtn} disabled={busy} onClick={() => fileRef.current?.click()}>
          {busy ? <Loader2 size={14} className={styles.spin} /> : <Upload size={14} />} Remplacer l'atlas
        </button>
        {url && (
          <a className={styles.secondaryBtn} href={url} download={`glyph_${page}.png`}>
            <Download size={14} /> Télécharger
          </a>
        )}
        <button
          className={styles.secondaryBtn}
          disabled={busy || publishing !== null}
          onClick={publish}
          title="Commit + push de l'atlas sur la branche de travail du pack, sans passer sur main"
        >
          {publishing === 'push' ? <Loader2 size={14} className={styles.spin} /> : <GitBranch size={14} />}
          Publier sur {branch ?? 'develop'}
        </button>
        <button
          className={styles.secondaryBtn}
          disabled={busy || publishing !== null || branch?.toLowerCase() === 'main'}
          onClick={deliver}
          title="PR vers main + merge + tag de release du pack"
        >
          {publishing === 'main' ? <Loader2 size={14} className={styles.spin} /> : <Rocket size={14} />}
          Mettre sur main
        </button>
      </div>

      {feedback && <span className={feedback.ok ? styles.okMsg : styles.error}>{feedback.msg}</span>}
      {log.length > 0 && (
        <div className={styles.essBlock}>
          {log.map((l, i) => <div key={i} className={styles.muted}>{l}</div>)}
        </div>
      )}
    </div>
  );
}
